import React, { useEffect, useState } from "react";
import { FiCheck, FiChevronLeft, FiChevronRight, FiHeart, FiMinus, FiPlus, FiX } from "react-icons/fi";
import { AddCartButton } from "../Buttons/AddCartButton";
import { ApplePayBuyButton } from "../Buttons/ApplePayBuyButton";
import defaultImage from "../../assets/png/no-image.png";
import { useProductDetail } from "../../hooks/useProductDetail";
import { useDetailState } from "../Detail/useDetailState";

interface ProductDetailModalProps {
  isOpen: boolean;
  productId: number | null;
  onClose: () => void;
}

const ProductDetailModalContent: React.FC<{
  productDetail: NonNullable<ReturnType<typeof useProductDetail>["productDetail"]>;
  productItems: ReturnType<typeof useProductDetail>["productItems"];
  onClose: () => void;
  onStockUpdate: NonNullable<ReturnType<typeof useProductDetail>["updateProductItemStock"]>;
}> = ({ productDetail, productItems, onClose, onStockUpdate }) => {
  const {
    selectedState,
    displaySizes,
    availableSizeIds,
    availableColors,
    carouselImages,
    handleStockUpdate,
    handleSelectColor,
    handleSelectSize,
    handleQuantityChange,
  } = useDetailState({
    productDetail,
    productItems,
    onStockUpdate,
  });

  const [activeImageIndex, setActiveImageIndex] = useState(0);

  useEffect(() => {
    setActiveImageIndex(0);
  }, [carouselImages]);

  const activeImage = carouselImages[activeImageIndex] || defaultImage;

  const handlePrevImage = () => {
    setActiveImageIndex((prev) =>
      prev === 0 ? carouselImages.length - 1 : prev - 1,
    );
  };

  const handleNextImage = () => {
    setActiveImageIndex((prev) =>
      prev === carouselImages.length - 1 ? 0 : prev + 1,
    );
  };

  return (
    <div className="flex h-full flex-col bg-white shadow-2xl md:max-w-[500px] md:rounded-l-[32px] lg:max-w-[520px]">
      <div className="flex items-start justify-between border-b border-[#ECECEE] px-5 py-4 md:px-6 md:py-5">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#FB2C36]">
            {productDetail.category?.name || "Product"}
          </p>
          <h2 className="mt-1 text-3xl font-bold text-[#17181C] md:text-[2rem]">
            Product Details
          </h2>
        </div>
        <button
          onClick={onClose}
          className="rounded-full p-2 text-[#5F6470] transition-colors hover:bg-[#F3F4F6] hover:text-black"
          aria-label="Close product details"
        >
          <FiX className="text-2xl" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="relative bg-[#F3F0EB]">
          <img
            src={activeImage}
            alt={productDetail.name}
            className="h-[320px] w-full object-cover md:h-[390px]"
          />

          {carouselImages.length > 1 && (
            <>
              <button
                onClick={handlePrevImage}
                className="absolute left-4 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-[#1F2937] shadow-md transition-transform hover:scale-[1.03]"
                aria-label="Previous image"
              >
                <FiChevronLeft className="text-xl" />
              </button>
              <button
                onClick={handleNextImage}
                className="absolute right-4 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-[#1F2937] shadow-md transition-transform hover:scale-[1.03]"
                aria-label="Next image"
              >
                <FiChevronRight className="text-xl" />
              </button>
            </>
          )}

          <button
            className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-white/95 text-[#5F6470] shadow-md transition-colors hover:text-[#FB2C36]"
            aria-label="Save product"
          >
            <FiHeart className="text-xl" />
          </button>

          {carouselImages.length > 1 && (
            <div className="absolute bottom-4 left-1/2 flex -translate-x-1/2 items-center gap-2">
              {carouselImages.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setActiveImageIndex(index)}
                  className={`h-2.5 rounded-full transition-all ${
                    index === activeImageIndex ? "w-5 bg-[#FB2C36]" : "w-2.5 bg-white/70"
                  }`}
                  aria-label={`View image ${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>

        <div className="px-5 py-5 md:px-6 md:py-6">
          <h3 className="text-[2rem] font-bold leading-tight text-[#17181C] md:text-[2.2rem]">
            {productDetail.name}
          </h3>

          <div className="mt-4 flex items-center gap-2 text-[#F8B400]">
            <span className="text-lg">★★★★★</span>
            <span className="text-sm font-medium text-[#6B7280]">
              {availableColors.length} colors available
            </span>
          </div>

          <p className="mt-3 text-5xl font-bold text-[#17181C] md:text-[3rem]">
            ${productDetail.price.toFixed(2)}
          </p>

          <div className="mt-6">
            <p className="text-sm font-extrabold uppercase tracking-[0.12em] text-[#17181C]">
              Description
            </p>
            <div
              className="mt-3 text-lg leading-8 text-[#4B5563]"
              dangerouslySetInnerHTML={{ __html: productDetail.description || "" }}
            />
          </div>

          <div className="mt-7">
            <p className="text-sm font-extrabold uppercase tracking-[0.12em] text-[#17181C]">
              Select Color
            </p>
            {availableColors.length === 0 ? (
              <div className="mt-4 rounded-lg bg-[#FEF3F4] p-4 text-center">
                <p className="text-sm text-[#DC2626] font-medium">
                  ⚠️ This product has no color options configured yet.
                </p>
                <p className="text-xs text-[#9CA3AF] mt-2">
                  Please contact support or try another product.
                </p>
              </div>
            ) : (
              <div className="mt-4 flex flex-wrap gap-3">
                {availableColors.map((color) => {
                  const isSelected = selectedState.colorId === color.id;
                  return (
                    <button
                      key={color.id}
                      onClick={() => color.id && handleSelectColor(color.id)}
                      className={`flex h-11 w-11 items-center justify-center rounded-full border-2 transition-transform ${
                        isSelected ? "border-[#FB2C36] scale-[1.03]" : "border-transparent"
                      }`}
                      style={{ backgroundColor: color.color || "#D1D5DB" }}
                      aria-label={`Select ${color.name}`}
                    >
                      {isSelected && <FiCheck className="text-white text-lg" />}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="mt-7">
            <p className="text-sm font-extrabold uppercase tracking-[0.12em] text-[#17181C]">
              Select Size
            </p>
            {displaySizes.length === 0 ? (
              <div className="mt-4 rounded-lg bg-[#FEF3F4] p-4 text-center">
                <p className="text-sm text-[#DC2626] font-medium">
                  ⚠️ This product has no size options configured yet.
                </p>
                <p className="text-xs text-[#9CA3AF] mt-2">
                  Please contact support or try another product.
                </p>
              </div>
            ) : (
              <div className="mt-4 flex flex-wrap gap-3">
                {displaySizes.map((size) => {
                  const isAvailable = size.id ? availableSizeIds.has(size.id) : false;
                  const isSelected = selectedState.sizeId === size.id;
                  return (
                    <button
                      key={size.id}
                      onClick={() => isAvailable && size.id && handleSelectSize(size.id)}
                      className={`min-w-[64px] rounded-2xl border px-5 py-3 text-lg font-semibold transition-colors ${
                        isSelected
                          ? "border-[#FB2C36] bg-[#FB2C36] text-white"
                          : isAvailable
                            ? "border-[#D8DCE4] bg-white text-[#30343F]"
                            : "border-[#E5E7EB] bg-[#F3F4F6] text-[#9CA3AF]"
                      }`}
                      disabled={!isAvailable}
                    >
                      {size.name}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="mt-7">
            <p className="text-sm font-extrabold uppercase tracking-[0.12em] text-[#17181C]">
              Quantity
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-4 rounded-full border border-[#D8DCE4] bg-white px-3 py-2">
                <button
                  onClick={() => handleQuantityChange(Math.max(selectedState.quantity - 1, 0))}
                  className="flex h-8 w-8 items-center justify-center rounded-full border border-[#E5E7EB] text-[#4B5563]"
                  aria-label="Decrease quantity"
                >
                  <FiMinus />
                </button>
                <span className="min-w-[20px] text-center text-2xl font-semibold text-[#17181C]">
                  {selectedState.quantity}
                </span>
                <button
                  onClick={() => handleQuantityChange(selectedState.quantity + 1)}
                  className="flex h-8 w-8 items-center justify-center rounded-full border border-[#E5E7EB] text-[#4B5563]"
                  aria-label="Increase quantity"
                  disabled={selectedState.quantity >= selectedState.stock}
                >
                  <FiPlus />
                </button>
              </div>
              <p className="text-lg text-[#4B5563]">
                {selectedState.stock > 0 ? (
                  <>
                    In Stock <span className="text-[#16A34A]">{selectedState.stock}</span>
                  </>
                ) : (
                  <span className="text-[#DC2626]">No stock available</span>
                )}
              </p>
            </div>
          </div>

          {productDetail.care && (
            <div className="mt-7">
              <p className="text-sm font-extrabold uppercase tracking-[0.12em] text-[#17181C]">
                Care Instructions
              </p>
              <div
                className="mt-3 text-lg leading-8 text-[#4B5563]"
                dangerouslySetInnerHTML={{ __html: productDetail.care || "" }}
              />
            </div>
          )}

          {productDetail.details && (
            <div className="mt-7">
              <p className="text-sm font-extrabold uppercase tracking-[0.12em] text-[#17181C]">
                Details
              </p>
              <div
                className="mt-3 text-lg leading-8 text-[#4B5563]"
                dangerouslySetInnerHTML={{ __html: productDetail.details || "" }}
              />
            </div>
          )}
        </div>
      </div>

      <div className="border-t border-[#E5E7EB] bg-white px-5 py-4 md:px-6 md:py-5">
        <div className="mb-4 flex items-center justify-between text-[#17181C]">
          <span className="text-2xl font-medium">Total:</span>
          <span className="text-[2rem] font-bold">
            ${(productDetail.price * selectedState.quantity).toFixed(2)}
          </span>
        </div>

        <div className="flex flex-wrap justify-center gap-3">
                <ApplePayBuyButton
                  product={productDetail}
                  quantity={selectedState.quantity}
                  selectedSizeId={selectedState.sizeId}
                  selectedColorId={selectedState.colorId}
                  stock={selectedState.stock}
                  onStockUpdate={handleStockUpdate}
                  onClose={onClose}
                />
                <AddCartButton
                  product={productDetail}
                  quantity={selectedState.quantity}
                  selectedSizeId={selectedState.sizeId}
                  selectedColorId={selectedState.colorId}
                  stock={selectedState.stock}
                  onStockUpdate={handleStockUpdate}
                />
        </div>

        <button
          onClick={onClose}
          className="mt-4 w-full text-center text-base font-medium text-[#3F3F46] transition-colors hover:text-black"
        >
          Continue Shopping
        </button>
      </div>
    </div>
  );
};

const ProductDetailModal: React.FC<ProductDetailModalProps> = ({
  isOpen,
  productId,
  onClose,
}) => {
  const { productDetail, productItems, loading, error, updateProductItemStock } =
    useProductDetail(productId ?? 0, isOpen && Boolean(productId));

  useEffect(() => {
    if (!isOpen) {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
      return;
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    // Prevent scrolling on body and html elements when modal is open
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end bg-black/45 backdrop-blur-[2px]">
      <button
        onClick={onClose}
        className="hidden flex-1 md:block"
        aria-label="Close product detail overlay"
      />

      <div className="h-full w-full max-w-[540px]">
        {loading && (
          <div className="flex h-full items-center justify-center bg-white text-lg font-semibold text-[#17181C] md:rounded-l-[32px]">
            Loading product...
          </div>
        )}

        {!loading && error && (
          <div className="flex h-full flex-col items-center justify-center bg-white px-6 text-center md:rounded-l-[32px]">
            <p className="text-xl font-semibold text-[#17181C]">
              We couldn&apos;t load this product.
            </p>
            <button
              onClick={onClose}
              className="mt-4 rounded-full bg-[#FB2C36] px-5 py-3 font-semibold text-white"
            >
              Close
            </button>
          </div>
        )}

        {!loading && !error && productDetail && (
          <ProductDetailModalContent
            productDetail={productDetail}
            productItems={productItems}
            onClose={onClose}
            onStockUpdate={updateProductItemStock}
          />
        )}
      </div>
    </div>
  );
};

export default ProductDetailModal;